import { useState } from "react";
import { visit } from "@hotwired/turbo";
import { useApiRequest } from "../../hooks/useApiRequest";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronDown, Check } from "lucide-react";
import { Brain, Avatar } from "../../types/brains.d";

interface BrainCreateProps {
  brain: Brain;
  avatars: Avatar[];
}

// #STYLE_GUIDE: Create component for new record creation
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
// - API calls via useApiRequest hook for consistency
// #TODO: This is a basic implementation - customize form fields and validation as needed
export default function BrainCreate({ brain, avatars }: BrainCreateProps) {
  // Get avatar_id from URL params if present
  const urlParams = new URLSearchParams(window.location.search);
  const avatarIdFromUrl = urlParams.get("avatar_id");

  // Pre-select avatar if avatar_id is in URL
  const initialAvatar = avatarIdFromUrl
    ? avatars.find((a) => a.id === avatarIdFromUrl) || null
    : null;

  const [formData, setFormData] = useState({
    name: "",
    content: "",
    enabled: true, // Default to enabled
    avatar_id: avatarIdFromUrl || "",
  });

  const [selectedAvatar, setSelectedAvatar] = useState<Avatar | null>(
    initialAvatar
  );
  const [avatarQuery, setAvatarQuery] = useState("");

  const { loading, makeRequest, getFieldError } = useApiRequest({
    onSuccess: (data) => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit(`/brains/${data.id}`);
    },
    onError: (error) => {
      console.error("Failed to create brain:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      avatar_id: selectedAvatar?.id || "",
    };

    await makeRequest("POST", "/api/v1/brains", {
      brain: submitData,
    });
  };

  const handleCancel = () => {
    // #TURBO: Use Turbo.visit for navigation
    // If we have an avatar_id from URL, go back to that avatar, otherwise go to dashboard
    if (avatarIdFromUrl) {
      visit(`/avatars/${avatarIdFromUrl}`);
    } else {
      visit("/dashboard/index");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Create New Brain</h1>
        <button onClick={handleCancel} className="btn btn-outline">
          Cancel
        </button>
      </div>

      {/* Form */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Name</span>
              </label>
              <input
                type="text"
                className={`input input-bordered ${
                  getFieldError("name") ? "input-error" : ""
                }`}
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />
              {getFieldError("name") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("name")}
                  </span>
                </label>
              )}
            </div>

            {/* Content field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Content</span>
              </label>
              <textarea
                className={`textarea textarea-bordered ${
                  getFieldError("content") ? "textarea-error" : ""
                }`}
                placeholder="Enter content"
                value={formData.content}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                rows={4}
              />
              {getFieldError("content") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("content")}
                  </span>
                </label>
              )}
            </div>

            {/* Enabled field */}
            <div className="form-control">
              <label className="label cursor-pointer">
                <span className="label-text">Enabled</span>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={formData.enabled}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                />
              </label>
              {getFieldError("enabled") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("enabled")}
                  </span>
                </label>
              )}
            </div>

            {/* Avatar field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Avatar</span>
              </label>
              <div className="relative">
                <Listbox
                  value={selectedAvatar}
                  onChange={(value) => {
                    setSelectedAvatar(value);
                    setFormData((prev) => ({
                      ...prev,
                      avatar_id: value?.id || "",
                    }));
                  }}
                >
                  <ListboxButton
                    className={`input input-bordered w-full text-left ${
                      getFieldError("avatar_id") ? "input-error" : ""
                    }`}
                  >
                    {selectedAvatar?.displayName || "Select avatar"}
                    <ChevronDown className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none w-5 h-5" />
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 w-full bg-base-100 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {(() => {
                      const filteredAvatar = avatars.filter((item) =>
                        item.displayName
                          .toLowerCase()
                          .includes(avatarQuery.toLowerCase())
                      );

                      if (filteredAvatar.length === 0) {
                        return (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            No avatar found.
                          </div>
                        );
                      }

                      return filteredAvatar.map((item) => (
                        <ListboxOption
                          key={item.id}
                          value={item}
                          className="relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-base-200"
                        >
                          {item.displayName}
                          {selectedAvatar?.id === item.id && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </ListboxOption>
                      ));
                    })()}
                  </ListboxOptions>
                </Listbox>
              </div>
              {getFieldError("avatar_id") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("avatar_id")}
                  </span>
                </label>
              )}
            </div>

            {/* Submit buttons */}
            <div className="form-control mt-6">
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Creating...
                    </>
                  ) : (
                    "Create Brain"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-outline"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
